import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Camera, CheckCircle2, ChevronRight, FileText, Image as ImageIcon, Paperclip, Pencil, RotateCcw, ShieldCheck, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Note } from '@/components/app/Badges';
import { EventStatusPill, Field, FormSheet, OperationalPill, PriorityPill, RefSelect, RefsInlineStatus, SelectField, ServiceStatusPill, SheetSection, StatusPill, formatHistoryTime, inputClass, useDriftRefs } from '@/components/app/DriftUI';
import {
  ATTACHMENT_RULES, EVENT_STATUS_LABELS, EVENT_TYPE_LABELS, PRIORITIES, PRIORITY_LABELS, addEventNote, attachmentProblem, attachmentUrl, createEvent, deleteAttachment, deleteEvent, fileSizeText, followUpReasons,
  getEvent, nextFaultStatus, patchEvent, releaseMachine, reopenEvent, resolveEvent, serviceLine, uploadAttachments,
} from '@/lib/driftApi';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort, osloDateOf } from '@/domain/time';
import { cn } from '@/lib/utils';

/*
  Maskinsaker – feilmeldinger, service og vedlikehold på maskinparken (EKSEMPELDATA).
  - «Meld feil»: maskin, kort tittel, beskrivelse, dato, prosjekt, vedlegg, «Kan ikke brukes», tildelt, prioritet, frist.
  - Saksgang: innmeldt → mottatt → under utbedring → løst. Løst ≠ kan brukes: «kan ikke brukes» krever eksplisitt frigivelse.
  - Service/vedlikehold: planlagt → utført. Utført service (med målerstand) oppdaterer maskinens eksempelplan.
  - Alt lagres med historikk. Ingen melding sendes til noen. Vedlegg er syntetiske eksempelfiler i databasen.
*/

const today = () => osloDateOf(new Date());
const dateText = (d) => (d ? formatDateShort(d) : '–');
/** Dato i lister: for avsluttede saker vises dato for løst/utført (det som faktisk skjedde), ellers meldt/planlagt dato. */
const listDate = (ev) => (!ev.open && ev.resolution?.date ? ev.resolution.date : ev.date);
const listDateTitle = (ev) => (!ev.open && ev.resolution?.date ? `${ev.type === 'feilmelding' ? 'Løst' : 'Utført'} ${dateText(ev.resolution.date)} · ${ev.type === 'feilmelding' ? 'meldt' : 'planlagt'} ${dateText(ev.date)}` : undefined);
const meterUnit = (m) => (m?.meterType === 'km' ? 'km' : 't');
const machineLabel = (m) => (m ? `${m.internalNo} · ${m.name}` : '');
const primaryBtn = 'rounded-[10px] bg-[var(--hr-heading)] text-white shadow-none hover:bg-[var(--hr-navy)]';
const textareaClass = 'min-h-[88px] rounded-[10px] border-[var(--hr-border)] bg-white text-[16px] shadow-none lg:text-[14px]';

const errText = (err) => err?.message ?? 'Ukjent feil';

/* ---------- Små byggeklosser ---------- */

/** Flaggene på en sak: type, status, prioritet (feil), «Kan ikke brukes», frist. */
export function CaseBadges({ ev, compact = false, className }) {
  return (
    <span className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <EventStatusPill status={ev.status} data-testid={`case-status-${ev.id}`} />
      {ev.type === 'feilmelding' && ev.priority !== 'normal' && <PriorityPill priority={ev.priority} data-testid={`case-priority-${ev.id}`} />}
      {ev.blocksUse && ev.open && (
        <StatusPill tone="red" data-testid={`case-blocks-${ev.id}`}>
          Kan ikke brukes
        </StatusPill>
      )}
      {ev.awaitingClearance && (
        <StatusPill tone="amber" data-testid={`case-awaiting-${ev.id}`}>
          Avventer frigivelse
        </StatusPill>
      )}
      {ev.overdue && (
        <StatusPill tone="amber" data-testid={`case-overdue-${ev.id}`}>
          Frist passert
        </StatusPill>
      )}
      {!compact && ev.attachments?.length > 0 && (
        <span className="inline-flex items-center gap-1 text-[12px] text-[var(--hr-muted)]" title={`${ev.attachments.length} vedlegg`}>
          <Paperclip className="h-3 w-3" aria-hidden="true" />
          {ev.attachments.length}
        </span>
      )}
    </span>
  );
}

function TypeMark({ type }) {
  const tone = type === 'feilmelding' ? 'text-[var(--hr-red)] bg-[var(--hr-red-bg)]' : 'text-[var(--hr-navy)] bg-[rgba(29,40,86,0.06)]';
  return <span className={cn('inline-flex h-5 items-center rounded-[6px] px-1.5 text-[12px] font-semibold uppercase tracking-[0.04em]', tone)}>{EVENT_TYPE_LABELS[type] ?? type}</span>;
}

/** Prioritetsvelger som store trykkflater (mobil) – ikke nedtrekk. */
function PriorityPicker({ value, onChange, testId = 'priority' }) {
  const tone = { lav: 'data-[on=true]:bg-[var(--hr-surface-2)]', normal: 'data-[on=true]:bg-[var(--hr-surface-2)]', hoy: 'data-[on=true]:border-[rgba(154,91,0,0.45)] data-[on=true]:bg-[var(--hr-amber-bg)] data-[on=true]:text-[var(--hr-amber)]', kritisk: 'data-[on=true]:border-[rgba(180,35,24,0.45)] data-[on=true]:bg-[var(--hr-red-bg)] data-[on=true]:text-[var(--hr-red)]' };
  return (
    <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Prioritet" data-testid={testId}>
      {PRIORITIES.map((p) => (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={value === p}
          data-on={value === p}
          onClick={() => onChange(p)}
          className={cn('h-10 rounded-[10px] border border-[var(--hr-border)] bg-white text-[14px] font-medium text-[var(--hr-text)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] data-[on=true]:border-[var(--hr-heading)] data-[on=true]:font-semibold data-[on=true]:text-[var(--hr-heading)]', tone[p])}
          data-testid={`${testId}-${p}`}
        >
          {PRIORITY_LABELS[p]}
        </button>
      ))}
    </div>
  );
}

/** Tydelig «Kan ikke brukes»-bryter. */
function CannotUseSwitch({ checked, onChange, testId = 'cannot-use' }) {
  return (
    <label className={cn('flex cursor-pointer items-center justify-between gap-3 rounded-[12px] border px-4 py-3 transition-colors duration-150', checked ? 'border-[rgba(180,35,24,0.35)] bg-[var(--hr-red-bg)]' : 'border-[var(--hr-border)] bg-white')} data-testid={`${testId}-field`}>
      <span className="flex min-w-0 items-start gap-3">
        <AlertTriangle className={cn('mt-0.5 h-5 w-5 shrink-0', checked ? 'text-[var(--hr-red)]' : 'text-[var(--hr-muted-2)]')} aria-hidden="true" />
        <span className="min-w-0">
          <span className={cn('block text-[14px] font-semibold', checked ? 'text-[var(--hr-red)]' : 'text-[var(--hr-heading)]')}>Kan ikke brukes</span>
          <span className="block text-[13px] leading-5 text-[var(--hr-muted)]">Maskinen settes som «kan ikke brukes» til saken er løst og maskinen er frigitt.</span>
        </span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label="Kan ikke brukes" className="data-[state=checked]:bg-[var(--hr-red)]" data-testid={testId} />
    </label>
  );
}

/** Velg filer (bilde/PDF) med klientsjekk. Viser valgte filer før de lastes opp. */
export function FilePicker({ files, onChange, existingCount = 0, testId = 'attachments' }) {
  const ref = useRef(null);
  const pick = (list) => {
    const next = [...files];
    for (const f of Array.from(list ?? [])) {
      const problem = attachmentProblem(f, existingCount + next.length);
      if (problem) {
        toast.error('Vedlegget ble ikke lagt til', { description: problem });
        continue;
      }
      next.push(f);
    }
    onChange(next);
    if (ref.current) ref.current.value = '';
  };
  return (
    <div className="space-y-2">
      <input ref={ref} type="file" accept={ATTACHMENT_RULES.accept} multiple className="sr-only" onChange={(e) => pick(e.target.files)} data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" className="h-11 w-full justify-center rounded-[10px] bg-white text-[14px] lg:h-10" onClick={() => ref.current?.click()} data-testid={`${testId}-pick`}>
        <Camera className="mr-2 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Legg til bilde eller PDF
      </Button>
      <p className="text-[13px] text-[var(--hr-muted-2)]">{ATTACHMENT_RULES.label}. Kun syntetiske eksempelfiler.</p>
      {files.length > 0 && (
        <ul className="space-y-1.5" data-testid={`${testId}-list`}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-[10px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3 py-2 text-[14px]">
              {f.type === 'application/pdf' ? <FileText className="h-4 w-4 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" /> : <ImageIcon className="h-4 w-4 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" />}
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="hr-tabular text-[13px] text-[var(--hr-muted-2)]">{fileSizeText(f.size)}</span>
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--hr-muted)] hover:bg-white hover:text-[var(--hr-text)]" aria-label={`Fjern ${f.name}`} data-testid={`${testId}-remove-${i}`}>
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PersonSelect({ id, value, onChange, refs, refsState, emptyLabel = 'Ikke oppgitt', testId }) {
  return (
    <>
      <RefSelect id={id} value={value} onValueChange={onChange} allowEmpty emptyLabel={emptyLabel} list={refs?.employees} getHint={(x) => x.role} testId={testId} />
      {refsState && <RefsInlineStatus refs={refs} error={refsState.error} loading={refsState.loading} onRetry={refsState.retry} testId={`${testId}-refs`} />}
    </>
  );
}

/** Laster opp vedlegg etter at saken er opprettet – feil her stopper ikke saken. */
async function uploadAfterCreate(created, files) {
  if (!files.length) return created;
  try {
    return await uploadAttachments(created.id, files);
  } catch (err) {
    toast.error('Saken er lagret, men vedlegg feilet', { description: errText(err) });
    return created;
  }
}

/* ---------- Meld feil ---------- */

const emptyFault = (machineId) => ({ machineId: machineId ?? '', title: '', description: '', date: today(), projectId: '', cannotUse: false, assignedToId: '', priority: 'normal', dueDate: '', reportedById: '', meterAt: '' });

export function MeldFeilSheet({ open, onOpenChange, machines, machineId, onSaved }) {
  const refsState = useDriftRefs();
  const { refs } = refsState;
  const [form, setForm] = useState(() => emptyFault(machineId));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(emptyFault(machineId));
      setFiles([]);
      setErrors({});
    }
  }, [open, machineId]);
  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const machine = machines.find((m) => m.machineId === form.machineId);

  const save = async () => {
    const e = {};
    if (!form.machineId) e.machineId = 'Velg maskin';
    if (form.title.trim().length < 3) e.title = 'Skriv en kort tittel (minst 3 tegn)';
    if (!form.date) e.date = 'Velg dato';
    if (form.dueDate && form.dueDate < form.date) e.dueDate = 'Fristen kan ikke være før meldt dato';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      let created = await createEvent(form.machineId, {
        type: 'feilmelding',
        title: form.title.trim(),
        description: form.description.trim() || null,
        date: form.date,
        projectId: form.projectId || null,
        reportedById: form.reportedById || null,
        assignedToId: form.assignedToId || null,
        priority: form.priority,
        dueDate: form.dueDate || null,
        cannotUse: form.cannotUse,
        meterAt: form.meterAt === '' ? null : Number(form.meterAt),
      });
      created = await uploadAfterCreate(created, files);
      toast.success('Feil meldt', { description: `${machineLabel(machine)} · ${form.cannotUse ? 'Maskinen er satt som «kan ikke brukes»' : 'Innmeldt'}${created.assignedTo ? ` · tildelt ${created.assignedTo.name}` : ''}. Ingen melding sendes (eksempeldata).` });
      onOpenChange(false);
      onSaved?.(created);
    } catch (err) {
      toast.error('Kunne ikke melde feilen', { description: errText(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Meld feil"
      description="Det viktigste først: maskin, hva som er galt og om maskinen kan brukes. Eksempeldata – ingen melding sendes til noen."
      testId="fault-sheet"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" className="h-11 rounded-[10px] lg:h-10" onClick={() => onOpenChange(false)} disabled={saving} data-testid="fault-cancel">
            Avbryt
          </Button>
          <Button className={cn(primaryBtn, 'h-11 px-5 text-[14px] font-semibold lg:h-10')} onClick={save} disabled={saving} data-testid="fault-save">
            {saving ? 'Lagrer …' : 'Meld feil'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Maskin" htmlFor="ff-machine" required error={errors.machineId}>
          <SelectField id="ff-machine" value={form.machineId} onValueChange={set('machineId')} options={machines.map((m) => ({ value: m.machineId, label: machineLabel(m), hint: m.location }))} testId="fault-machine" />
        </Field>
        <Field label="Kort tittel" htmlFor="ff-title" required error={errors.title} hint="F.eks. «Hydraulikklekkasje ved tippsylinder»">
          <Input id="ff-title" value={form.title} onChange={(e) => set('title')(e.target.value)} maxLength={80} placeholder="Hva er galt?" className={inputClass} data-testid="fault-title" autoFocus />
        </Field>
        <CannotUseSwitch checked={form.cannotUse} onChange={set('cannotUse')} testId="fault-cannot-use" />
        <Field label="Beskrivelse" htmlFor="ff-desc" hint="Hva ble observert, når og hvor på maskinen?">
          <Textarea id="ff-desc" value={form.description} onChange={(e) => set('description')(e.target.value)} rows={3} maxLength={2000} className={textareaClass} data-testid="fault-description" />
        </Field>
        <Field label="Vedlegg" htmlFor="ff-files">
          <FilePicker files={files} onChange={setFiles} testId="fault-attachments" />
        </Field>
        <Field label="Prioritet" required>
          <PriorityPicker value={form.priority} onChange={set('priority')} testId="fault-priority" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dato" htmlFor="ff-date" required error={errors.date}>
            <Input id="ff-date" type="date" value={form.date} max={today()} onChange={(e) => set('date')(e.target.value)} className={inputClass} data-testid="fault-date" />
          </Field>
          <Field label="Frist" htmlFor="ff-due" hint="Valgfritt" error={errors.dueDate}>
            <Input id="ff-due" type="date" value={form.dueDate} min={form.date} onChange={(e) => set('dueDate')(e.target.value)} className={inputClass} data-testid="fault-due" />
          </Field>
        </div>
        <SheetSection title="Ansvar og tilknytning" description="Fiktive demomedarbeidere. Ingen varsling sendes.">
          <div className="space-y-4">
            <Field label="Tildelt" htmlFor="ff-assigned">
              <PersonSelect id="ff-assigned" value={form.assignedToId} onChange={set('assignedToId')} refs={refs} refsState={refsState} emptyLabel="Ikke tildelt ennå" testId="fault-assigned" />
            </Field>
            <Field label="Meldt av" htmlFor="ff-reporter">
              <PersonSelect id="ff-reporter" value={form.reportedById} onChange={set('reportedById')} refs={refs} testId="fault-reporter" />
            </Field>
            <Field label="Prosjekt" htmlFor="ff-project" hint="Valgfritt – hvor skjedde det?">
              <RefSelect id="ff-project" value={form.projectId} onValueChange={set('projectId')} allowEmpty emptyLabel="Ikke knyttet til prosjekt" list={refs?.projects} testId="fault-project" />
            </Field>
            <Field label={machine?.meterType === 'km' ? 'Km-stand ved feil' : 'Timeteller ved feil'} htmlFor="ff-meter" hint="Valgfritt – registerets teller, ikke registrert maskintid">
              <Input id="ff-meter" type="number" inputMode="numeric" min={0} value={form.meterAt} onChange={(e) => set('meterAt')(e.target.value)} placeholder={machine?.meter !== null && machine?.meter !== undefined ? fmtNumber(machine.meter, 0) : ''} className={inputClass} data-testid="fault-meter" />
            </Field>
          </div>
        </SheetSection>
      </div>
    </FormSheet>
  );
}

/* ---------- Planlegg / registrer service og vedlikehold ---------- */

const emptyService = (machineId) => ({ machineId: machineId ?? '', type: 'service', mode: 'planlagt', title: '', description: '', date: today(), dueDate: '', assignedToId: '', performedById: '', projectId: 'P-1005', meterAt: '', work: '' });

export function ServiceSheet({ open, onOpenChange, machines, machineId, onSaved }) {
  const refsState = useDriftRefs();
  const { refs } = refsState;
  const [form, setForm] = useState(() => emptyService(machineId));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(emptyService(machineId));
      setFiles([]);
      setErrors({});
    }
  }, [open, machineId]);
  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const machine = machines.find((m) => m.machineId === form.machineId);
  const planKind = machine?.servicePlan?.kind ?? machine?.meterType;
  const meterRequired = form.mode === 'utfort' && form.type === 'service' && (planKind === 'h' || planKind === 'km');
  const unit = meterUnit(machine);

  const save = async () => {
    const e = {};
    if (!form.machineId) e.machineId = 'Velg maskin';
    if (form.title.trim().length < 3) e.title = 'Skriv en kort tittel (minst 3 tegn)';
    if (!form.date) e.date = form.mode === 'utfort' ? 'Når ble arbeidet utført?' : 'Velg planlagt dato';
    if (form.mode === 'utfort' && form.work.trim().length < 3) e.work = 'Beskriv hva som ble gjort';
    if (meterRequired && form.meterAt === '') e.meterAt = `Oppgi ${unit === 'km' ? 'km-stand' : 'timeteller'} – planen er ${unit === 'km' ? 'km' : 'timer'}-basert`;
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      let created = await createEvent(form.machineId, {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim() || null,
        date: form.date,
        status: form.mode,
        projectId: form.projectId || null,
        assignedToId: form.assignedToId || null,
        performedById: form.performedById || form.assignedToId || null,
        dueDate: form.mode === 'planlagt' ? form.dueDate || form.date : null,
        meterAt: form.meterAt === '' ? null : Number(form.meterAt),
        work: form.mode === 'utfort' ? form.work.trim() : null,
      });
      created = await uploadAfterCreate(created, files);
      toast.success(form.mode === 'utfort' ? `${EVENT_TYPE_LABELS[form.type]} registrert som utført` : `${EVENT_TYPE_LABELS[form.type]} planlagt`, { description: `${machineLabel(machine)} · ${dateText(form.date)}${form.mode === 'utfort' && form.type === 'service' ? ' · eksempelplanen er oppdatert' : ''}` });
      onOpenChange(false);
      onSaved?.(created);
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: errText(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Service og vedlikehold"
      description="Planlegg kommende service, eller registrer arbeid som allerede er utført. Eksempeldata."
      testId="service-sheet"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" className="h-11 rounded-[10px] lg:h-10" onClick={() => onOpenChange(false)} disabled={saving} data-testid="service-cancel">
            Avbryt
          </Button>
          <Button className={cn(primaryBtn, 'h-11 px-5 text-[14px] font-semibold lg:h-10')} onClick={save} disabled={saving} data-testid="service-save">
            {saving ? 'Lagrer …' : form.mode === 'utfort' ? 'Registrer utført' : 'Planlegg'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Maskin" htmlFor="sf-machine" required error={errors.machineId}>
          <SelectField id="sf-machine" value={form.machineId} onValueChange={set('machineId')} options={machines.map((m) => ({ value: m.machineId, label: machineLabel(m), hint: m.location }))} testId="service-machine" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type" htmlFor="sf-type" required>
            <SelectField id="sf-type" value={form.type} onValueChange={set('type')} options={[{ value: 'service', label: 'Service' }, { value: 'vedlikehold', label: 'Vedlikehold' }]} testId="service-type" />
          </Field>
          <Field label="Status" htmlFor="sf-mode" required>
            <SelectField id="sf-mode" value={form.mode} onValueChange={set('mode')} options={[{ value: 'planlagt', label: 'Planlagt' }, { value: 'utfort', label: 'Allerede utført' }]} testId="service-mode" />
          </Field>
        </div>
        <Field label="Kort tittel" htmlFor="sf-title" required error={errors.title} hint={form.type === 'service' ? 'F.eks. «500-timers service»' : 'F.eks. «Dekkskift og bremsekontroll»'}>
          <Input id="sf-title" value={form.title} onChange={(e) => set('title')(e.target.value)} maxLength={80} className={inputClass} data-testid="service-title" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={form.mode === 'utfort' ? 'Utført dato' : 'Planlagt dato'} htmlFor="sf-date" required error={errors.date}>
            <Input id="sf-date" type="date" value={form.date} max={form.mode === 'utfort' ? today() : undefined} onChange={(e) => set('date')(e.target.value)} className={inputClass} data-testid="service-date" />
          </Field>
          <Field label={unit === 'km' ? 'Km-stand' : 'Timeteller'} htmlFor="sf-meter" required={meterRequired} error={errors.meterAt} hint={meterRequired ? 'Kreves for å beregne neste frist' : 'Valgfritt'}>
            <Input id="sf-meter" type="number" inputMode="numeric" min={0} value={form.meterAt} onChange={(e) => set('meterAt')(e.target.value)} placeholder={machine?.meter !== null && machine?.meter !== undefined ? fmtNumber(machine.meter, 0) : ''} className={inputClass} data-testid="service-meter" />
          </Field>
        </div>
        {form.mode === 'utfort' ? (
          <Field label="Utført arbeid" htmlFor="sf-work" required error={errors.work}>
            <Textarea id="sf-work" value={form.work} onChange={(e) => set('work')(e.target.value)} rows={3} maxLength={2000} placeholder="Olje, filtre, smøring …" className={textareaClass} data-testid="service-work" />
          </Field>
        ) : (
          <Field label="Hva skal gjøres" htmlFor="sf-desc" hint="Valgfritt">
            <Textarea id="sf-desc" value={form.description} onChange={(e) => set('description')(e.target.value)} rows={3} maxLength={2000} className={textareaClass} data-testid="service-description" />
          </Field>
        )}
        <Field label="Vedlegg" htmlFor="sf-files">
          <FilePicker files={files} onChange={setFiles} testId="service-attachments" />
        </Field>
        <SheetSection title="Ansvar og tilknytning" description="Fiktive demomedarbeidere. Ingen varsling sendes.">
          <div className="space-y-4">
            <Field label={form.mode === 'utfort' ? 'Utført av' : 'Ansvarlig'} htmlFor="sf-assigned">
              <PersonSelect id="sf-assigned" value={form.assignedToId} onChange={set('assignedToId')} refs={refs} refsState={refsState} emptyLabel="Ikke tildelt" testId="service-assigned" />
            </Field>
            <Field label="Prosjekt" htmlFor="sf-project" hint="Service føres normalt på «Verksted og vedlikehold»">
              <RefSelect id="sf-project" value={form.projectId} onValueChange={set('projectId')} allowEmpty emptyLabel="Ikke knyttet til prosjekt" list={refs?.projects} testId="service-project" />
            </Field>
          </div>
        </SheetSection>
      </div>
    </FormSheet>
  );
}

/* ---------- Saksdetalj (ark) ---------- */

export function Fact({ label, children, testId, className }) {
  return (
    <div className={cn('min-w-0', className)} data-testid={testId}>
      <dt className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--hr-muted-2)]">{label}</dt>
      <dd className="mt-0.5 break-words text-[14px] leading-5 text-[var(--hr-text)]">{children}</dd>
    </div>
  );
}

export function AttachmentChip({ att, onDelete }) {
  const isImage = att.contentType?.startsWith('image/');
  return (
    <li className="group/att relative overflow-hidden rounded-[10px] border border-[var(--hr-hairline)] bg-white" data-testid={`attachment-${att.id}`}>
      <a href={attachmentUrl(att.id)} target="_blank" rel="noreferrer" className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`attachment-open-${att.id}`}>
        {isImage ? (
          <img src={attachmentUrl(att.id)} alt={att.name} className="h-24 w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-24 w-full items-center justify-center bg-[var(--hr-surface-2)]">
            <FileText className="h-8 w-8 text-[var(--hr-muted-2)]" aria-hidden="true" />
          </div>
        )}
        <div className="px-2.5 py-1.5">
          <div className="truncate text-[13px] font-medium text-[var(--hr-heading)]">{att.name}</div>
          <div className="hr-tabular text-[12px] text-[var(--hr-muted-2)]">{isImage ? 'Bilde' : 'PDF'} · {fileSizeText(att.size)}</div>
        </div>
      </a>
      {onDelete && (
        <button type="button" onClick={() => onDelete(att)} className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[var(--hr-muted)] shadow-[var(--hr-shadow-xs)] hover:text-[var(--hr-red)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" aria-label={`Fjern vedlegg ${att.name}`} data-testid={`attachment-delete-${att.id}`}>
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

/** Historikk + notater i én tidslinje (nyeste øverst). Notater/arbeid vises med fiktiv medarbeider; øvrige med «Kontor (eksempeldata)». */
function CaseTimeline({ ev }) {
  const items = useMemo(() => {
    const notes = (ev.notes ?? []).map((n) => ({ key: `n-${n.id}`, at: n.at, kind: n.kind === 'arbeid' ? 'Utført arbeid' : 'Notat', by: n.by?.name ?? 'Ikke oppgitt', text: n.text, date: n.date, tone: n.kind === 'arbeid' ? 'navy' : 'neutral' }));
    const KIND = { opprettet: 'Opprettet', status: 'Status endret', endret: 'Endret', lost: 'Markert løst', utfort: 'Registrert utført', frigitt: 'Maskinen frigitt', gjenapnet: 'Gjenåpnet', vedlegg: 'Vedlegg lagt til', vedlegg_fjernet: 'Vedlegg fjernet' };
    const hist = (ev.history ?? [])
      .filter((h) => h.kind !== 'notat' && h.kind !== 'arbeid')
      .map((h, i) => ({ key: `h-${i}`, at: h.at, kind: KIND[h.kind] ?? h.kind, by: h.by, text: h.note, reason: h.reason, changes: h.changes, tone: h.kind === 'frigitt' || h.kind === 'lost' || h.kind === 'utfort' ? 'green' : h.kind === 'gjenapnet' ? 'amber' : 'neutral' }));
    return [...notes, ...hist].sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [ev]);
  if (!items.length) return <p className="text-[14px] text-[var(--hr-muted)]">Ingen hendelser i saken ennå.</p>;
  const dot = { green: 'bg-[var(--hr-green)]', amber: 'bg-[var(--hr-amber)]', navy: 'bg-[var(--hr-navy)]', neutral: 'bg-[var(--hr-muted-2)]' };
  return (
    <ol className="relative space-y-3 border-l border-[var(--hr-hairline)] pl-4" data-testid="case-timeline">
      {items.map((it) => (
        <li key={it.key} className="relative" data-testid="case-timeline-item">
          <span className={cn('absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white', dot[it.tone])} aria-hidden="true" />
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px]">
            <span className="font-semibold text-[var(--hr-heading)]">{it.kind}</span>
            <span className="hr-tabular text-[var(--hr-muted)]">{formatHistoryTime(it.at)}</span>
            <span className="text-[var(--hr-muted-2)]">·</span>
            <span className="text-[var(--hr-muted)]">{it.by}</span>
          </div>
          {it.reason && <p className="mt-0.5 text-[13px] italic text-[var(--hr-text)]">«{it.reason}»</p>}
          {it.text && <p className="mt-0.5 text-[14px] leading-5 text-[var(--hr-text)]">{it.text}</p>}
          {it.changes?.length > 0 && (
            <dl className="mt-1 grid gap-0.5 text-[13px]">
              {it.changes.map((c) => (
                <div key={c.field} className="grid grid-cols-[minmax(0,110px)_minmax(0,1fr)] gap-2">
                  <dt className="truncate text-[var(--hr-muted)]">{c.label}</dt>
                  <dd className="min-w-0 break-words">
                    <span className="line-through decoration-[var(--hr-muted-2)]">{c.from ?? 'tom'}</span>
                    <span className="mx-1.5 text-[var(--hr-muted-2)]">→</span>
                    <span className="font-medium text-[var(--hr-heading)]">{c.to ?? 'tom'}</span>
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Hvilke handlinger som gjelder i sakens nåværende tilstand. */
function nextActions(ev) {
  const a = [];
  if (ev.type === 'feilmelding') {
    if (ev.open) {
      const nxt = nextFaultStatus(ev.status);
      if (nxt) a.push({ id: 'status', label: nxt === 'mottatt' ? 'Marker mottatt' : 'Start utbedring', status: nxt, primary: ev.status === 'innmeldt' });
      a.push({ id: 'resolve', label: 'Marker løst', primary: ev.status === 'under_utbedring' || ev.status === 'mottatt' });
    } else if (ev.awaitingClearance) {
      a.push({ id: 'release', label: 'Frigi maskinen', primary: true });
      a.push({ id: 'reopen', label: 'Gjenåpne' });
    } else {
      a.push({ id: 'reopen', label: 'Gjenåpne' });
    }
  } else if (ev.open) {
    a.push({ id: 'resolve', label: 'Registrer utført', primary: true });
  } else {
    a.push({ id: 'reopen', label: 'Gjenåpne' });
  }
  return a;
}

export function CaseSheet({ eventId, open, onOpenChange, onChanged, machine }) {
  const refsState = useDriftRefs();
  const { refs } = refsState;
  const [ev, setEv] = useState(null);
  const [status, setStatus] = useState('loading');
  const [mode, setMode] = useState(null); // 'resolve' | 'release' | 'edit' | 'note' | null
  const [busy, setBusy] = useState(false);
  const [resolveForm, setResolveForm] = useState({ work: '', date: today(), byId: '', meterAt: '' });
  const [releaseForm, setReleaseForm] = useState({ byId: '', note: '' });
  const [noteForm, setNoteForm] = useState({ text: '', byId: '', kind: 'notat' });
  const [editForm, setEditForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    try {
      const d = await getEvent(eventId);
      setEv(d);
      setStatus('ready');
    } catch (e) {
      setStatus(e?.status === 404 ? 'missing' : 'error');
    }
  }, [eventId]);

  useEffect(() => {
    if (open && eventId) {
      setStatus('loading');
      setMode(null);
      setErrors({});
      load();
    }
  }, [open, eventId, load]);

  const changed = (next) => {
    setEv(next);
    setMode(null);
    setErrors({});
    onChanged?.(next);
  };

  /** Kjører en handling mot saken; true ved suksess. Feil vises som toast med backendens beskjed. */
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const next = await fn();
      changed(next);
      if (okMsg) toast.success(okMsg.title, { description: okMsg.description });
      return true;
    } catch (err) {
      toast.error('Kunne ikke oppdatere saken', { description: errText(err) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const isService = ev?.type === 'service';
  const planKind = machine?.servicePlan?.kind ?? ev?.machine?.meterType;
  const meterRequired = isService && (planKind === 'h' || planKind === 'km');
  const unit = ev?.machine?.meterType === 'km' ? 'km' : 't';

  /** Oppdaterer løs-skjemaet og fjerner feilmeldingen for feltet så snart brukeren retter det. */
  const setResolve = (k, v) => {
    setResolveForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const startResolve = () => {
    setResolveForm({ work: '', date: today(), byId: ev.assignedToId ?? '', meterAt: '' });
    setErrors({});
    setMode('resolve');
  };
  const submitResolve = () => {
    const e = {};
    if (resolveForm.work.trim().length < 3) e.work = 'Beskriv hva som ble gjort';
    if (!resolveForm.date) e.date = 'Velg dato';
    if (meterRequired && resolveForm.meterAt === '') e.meterAt = `Oppgi ${unit === 'km' ? 'km-stand' : 'timeteller'} – planen er ${unit === 'km' ? 'km' : 'timer'}-basert`;
    setErrors(e);
    if (Object.keys(e).length) return;
    run(
      () => resolveEvent(ev.id, { work: resolveForm.work.trim(), date: resolveForm.date, byId: resolveForm.byId || null, meterAt: resolveForm.meterAt === '' ? null : Number(resolveForm.meterAt) }),
      ev.type === 'feilmelding' ? { title: 'Saken er markert løst', description: ev.cannotUse ? 'Maskinen er fortsatt «kan ikke brukes» til den frigis.' : undefined } : { title: `${EVENT_TYPE_LABELS[ev.type]} registrert som utført`, description: isService ? 'Eksempelplanen for service er oppdatert.' : undefined },
    );
  };
  const submitRelease = () => run(() => releaseMachine(ev.id, { byId: releaseForm.byId || null, note: releaseForm.note.trim() || null }), { title: 'Maskinen er frigitt', description: 'Driftsstatus settes til «I drift».' });
  const submitNote = async () => {
    if (noteForm.text.trim().length < 2) {
      setErrors({ note: 'Skriv et notat' });
      return;
    }
    const ok = await run(() => addEventNote(ev.id, { text: noteForm.text.trim(), byId: noteForm.byId || null, kind: noteForm.kind }), { title: noteForm.kind === 'arbeid' ? 'Utført arbeid lagt til' : 'Notat lagt til' });
    if (ok) setNoteForm({ text: '', byId: '', kind: 'notat' });
  };
  const startEdit = () => {
    setEditForm({ title: ev.title ?? '', description: ev.description ?? '', date: ev.date, assignedToId: ev.assignedToId ?? '', priority: ev.priority ?? 'normal', dueDate: ev.dueDate ?? '', projectId: ev.projectId ?? '', cannotUse: !!ev.cannotUse, meterAt: ev.meterAt ?? '', reason: '' });
    setErrors({});
    setMode('edit');
  };
  const submitEdit = () => {
    const body = {};
    const f = editForm;
    if (f.title.trim() !== (ev.title ?? '')) body.title = f.title.trim();
    if ((f.description ?? '') !== (ev.description ?? '')) body.description = f.description;
    if (f.date !== ev.date) body.date = f.date;
    if ((f.assignedToId || '') !== (ev.assignedToId || '')) body.assignedToId = f.assignedToId;
    if (ev.type === 'feilmelding' && f.priority !== ev.priority) body.priority = f.priority;
    if ((f.dueDate || '') !== (ev.dueDate || '')) body.dueDate = f.dueDate;
    if ((f.projectId || '') !== (ev.projectId || '')) body.projectId = f.projectId;
    if (ev.type === 'feilmelding' && f.cannotUse !== !!ev.cannotUse) body.cannotUse = f.cannotUse;
    const meter = f.meterAt === '' ? null : Number(f.meterAt);
    if (meter !== (ev.meterAt ?? null)) body.meterAt = meter;
    if (!Object.keys(body).length) {
      setMode(null);
      return;
    }
    if (f.title.trim().length < 3) {
      setErrors({ title: 'Tittelen må ha minst 3 tegn' });
      return;
    }
    if (f.reason.trim()) body.reason = f.reason.trim();
    run(() => patchEvent(ev.id, body), { title: 'Saken er oppdatert', description: 'Endringene ligger i historikken.' });
  };
  const changeStatus = (st) => run(() => patchEvent(ev.id, { status: st }), { title: `Status: ${EVENT_STATUS_LABELS[st]}` });
  const reopen = () => run(() => reopenEvent(ev.id, {}), { title: 'Saken er gjenåpnet', description: ev.cannotUse ? 'Maskinen er igjen «kan ikke brukes».' : undefined });
  const removeAttachment = (att) => run(async () => {
    await deleteAttachment(att.id);
    return getEvent(ev.id);
  }, { title: 'Vedlegg fjernet' });
  const addFiles = async (list) => {
    const ok = [];
    for (const f of Array.from(list ?? [])) {
      const problem = attachmentProblem(f, (ev.attachments?.length ?? 0) + ok.length);
      if (problem) toast.error('Vedlegget ble ikke lagt til', { description: problem });
      else ok.push(f);
    }
    if (fileRef.current) fileRef.current.value = '';
    if (!ok.length) return;
    await run(() => uploadAttachments(ev.id, ok), { title: ok.length === 1 ? 'Vedlegg lagt til' : `${ok.length} vedlegg lagt til` });
  };
  const remove = async () => {
    setBusy(true);
    try {
      await deleteEvent(ev.id);
      toast.success('Saken er slettet');
      setConfirmDelete(false);
      onOpenChange(false);
      onChanged?.(null);
    } catch (err) {
      toast.error('Kunne ikke slette', { description: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  const actions = ev ? nextActions(ev) : [];
  const footer =
    ev && status === 'ready' && !mode ? (
      <div className="flex flex-wrap items-center justify-end gap-2" data-testid="case-actions">
        {!ev.seeded && (
          <Button variant="ghost" className="mr-auto h-10 rounded-[10px] px-2 text-[14px] text-[var(--hr-muted)] hover:text-[var(--hr-red)]" onClick={() => setConfirmDelete(true)} disabled={busy} data-testid="case-delete">
            <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> Slett
          </Button>
        )}
        {actions.map((a) => (
          <Button
            key={a.id}
            variant={a.primary ? 'default' : 'outline'}
            className={cn('h-10 rounded-[10px] text-[14px]', a.primary ? `${primaryBtn} font-semibold` : 'bg-white')}
            disabled={busy}
            onClick={() => (a.id === 'status' ? changeStatus(a.status) : a.id === 'resolve' ? startResolve() : a.id === 'release' ? (setReleaseForm({ byId: ev.assignedToId ?? '', note: '' }), setMode('release')) : reopen())}
            data-testid={`case-action-${a.id}`}
          >
            {a.id === 'release' && <ShieldCheck className="mr-1.5 h-4 w-4" aria-hidden="true" />}
            {a.id === 'reopen' && <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />}
            {a.label}
          </Button>
        ))}
      </div>
    ) : null;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title={ev?.title ?? 'Sak'} description={ev ? `${EVENT_TYPE_LABELS[ev.type]} · ${machineLabel(ev.machine) || ev.machineId} · meldt ${dateText(ev.date)}` : 'Henter saken …'} testId="case-sheet" footer={footer}>
      {status === 'loading' && <p className="text-[14px] text-[var(--hr-muted)]" aria-live="polite">Henter saken …</p>}
      {status === 'missing' && <Note>Saken finnes ikke lenger.</Note>}
      {status === 'error' && (
        <Note tone="amber">
          Kunne ikke hente saken. <Button variant="link" className="h-auto px-1" onClick={load}>Prøv igjen</Button>
        </Note>
      )}
      {ev && status === 'ready' && (
        <div className="space-y-5" data-testid="case-detail">
          <div className="flex flex-wrap items-center gap-1.5">
            <TypeMark type={ev.type} />
            <CaseBadges ev={ev} compact />
          </div>

          {ev.blocksUse && ev.open && (
            <Note tone="red" data-testid="case-blocked-note">
              Maskinen er satt som <strong>kan ikke brukes</strong>. Den forblir slik til saken er løst <em>og</em> maskinen er frigitt eksplisitt.
            </Note>
          )}
          {ev.awaitingClearance && (
            <Note tone="amber" data-testid="case-awaiting-note">
              Saken er løst, men maskinen er <strong>ikke frigitt</strong>. Bekreft at den kan brukes før den tas i bruk igjen.
            </Note>
          )}
          {ev.usable && (
            <Note tone="green" data-testid="case-released-note">
              Frigitt {formatHistoryTime(ev.usable.at)} av {ev.usable.by?.name ?? 'ikke oppgitt'}{ev.usable.note ? ` – «${ev.usable.note}»` : ''}.
            </Note>
          )}

          {mode === 'resolve' && (
            <section className="rounded-[12px] border border-[var(--hr-border)] bg-[var(--hr-surface-2)] p-4" data-testid="case-resolve-form">
              <h3 className="text-[14px] font-semibold text-[var(--hr-heading)]">{ev.type === 'feilmelding' ? 'Marker løst' : 'Registrer utført'}</h3>
              <p className="mt-0.5 text-[13px] text-[var(--hr-muted)]">{ev.type === 'feilmelding' && ev.cannotUse ? 'Løst betyr ikke at maskinen kan brukes – frigivelse er et eget steg.' : isService ? 'Utført service oppdaterer eksempelplanen for neste service.' : 'Hva ble gjort, når og av hvem.'}</p>
              <div className="mt-3 space-y-3">
                <Field label="Utført arbeid" htmlFor="rs-work" required error={errors.work}>
                  <Textarea id="rs-work" value={resolveForm.work} onChange={(e) => setResolve('work', e.target.value)} rows={3} maxLength={2000} className={textareaClass} data-testid="case-resolve-work" autoFocus />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Dato" htmlFor="rs-date" required error={errors.date}>
                    <Input id="rs-date" type="date" value={resolveForm.date} max={today()} onChange={(e) => setResolve('date', e.target.value)} className={inputClass} data-testid="case-resolve-date" />
                  </Field>
                  <Field label={unit === 'km' ? 'Km-stand' : 'Timeteller'} htmlFor="rs-meter" required={meterRequired} error={errors.meterAt} hint={meterRequired ? 'Kreves for neste frist' : 'Valgfritt'}>
                    <Input id="rs-meter" type="number" inputMode="numeric" min={0} value={resolveForm.meterAt} onChange={(e) => setResolve('meterAt', e.target.value)} placeholder={machine?.meter !== null && machine?.meter !== undefined ? fmtNumber(machine.meter, 0) : ''} className={inputClass} data-testid="case-resolve-meter" />
                  </Field>
                </div>
                <Field label="Utført av" htmlFor="rs-by">
                  <PersonSelect id="rs-by" value={resolveForm.byId} onChange={(v) => setResolveForm((f) => ({ ...f, byId: v }))} refs={refs} refsState={refsState} testId="case-resolve-by" />
                </Field>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setMode(null)} disabled={busy} data-testid="case-resolve-cancel">Avbryt</Button>
                  <Button className={cn(primaryBtn, 'h-10 font-semibold')} onClick={submitResolve} disabled={busy} data-testid="case-resolve-save">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> {ev.type === 'feilmelding' ? 'Marker løst' : 'Registrer utført'}
                  </Button>
                </div>
              </div>
            </section>
          )}

          {mode === 'release' && (
            <section className="rounded-[12px] border border-[rgba(154,91,0,0.35)] bg-[var(--hr-amber-bg)] p-4" data-testid="case-release-form">
              <h3 className="text-[14px] font-semibold text-[var(--hr-heading)]">Frigi maskinen</h3>
              <p className="mt-0.5 text-[13px] text-[var(--hr-text)]">Bekreft at maskinen er kontrollert og kan brukes igjen. Lagres med hvem og når.</p>
              <div className="mt-3 space-y-3">
                <Field label="Frigitt av" htmlFor="rl-by">
                  <PersonSelect id="rl-by" value={releaseForm.byId} onChange={(v) => setReleaseForm((f) => ({ ...f, byId: v }))} refs={refs} refsState={refsState} testId="case-release-by" />
                </Field>
                <Field label="Kommentar" htmlFor="rl-note" hint="Valgfritt – f.eks. «Prøvekjørt 30 min»">
                  <Input id="rl-note" value={releaseForm.note} maxLength={500} onChange={(e) => setReleaseForm((f) => ({ ...f, note: e.target.value }))} className={inputClass} data-testid="case-release-note" />
                </Field>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setMode(null)} disabled={busy} data-testid="case-release-cancel">Avbryt</Button>
                  <Button className={cn(primaryBtn, 'h-10 font-semibold')} onClick={submitRelease} disabled={busy} data-testid="case-release-save">
                    <ShieldCheck className="mr-1.5 h-4 w-4" aria-hidden="true" /> Frigi maskinen
                  </Button>
                </div>
              </div>
            </section>
          )}

          {mode === 'edit' && editForm && (
            <section className="rounded-[12px] border border-[var(--hr-border)] bg-[var(--hr-surface-2)] p-4" data-testid="case-edit-form">
              <h3 className="text-[14px] font-semibold text-[var(--hr-heading)]">Rediger sak</h3>
              <p className="mt-0.5 text-[13px] text-[var(--hr-muted)]">Endringer lagres som før → etter i historikken.</p>
              <div className="mt-3 space-y-3">
                <Field label="Kort tittel" htmlFor="ed-title" required error={errors.title}>
                  <Input id="ed-title" value={editForm.title} maxLength={80} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} className={inputClass} data-testid="case-edit-title" />
                </Field>
                <Field label="Beskrivelse" htmlFor="ed-desc">
                  <Textarea id="ed-desc" value={editForm.description} rows={3} maxLength={2000} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} className={textareaClass} data-testid="case-edit-description" />
                </Field>
                {ev.type === 'feilmelding' && <CannotUseSwitch checked={editForm.cannotUse} onChange={(v) => setEditForm((f) => ({ ...f, cannotUse: v }))} testId="case-edit-cannot-use" />}
                {ev.type === 'feilmelding' && (
                  <Field label="Prioritet">
                    <PriorityPicker value={editForm.priority} onChange={(v) => setEditForm((f) => ({ ...f, priority: v }))} testId="case-edit-priority" />
                  </Field>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Dato" htmlFor="ed-date">
                    <Input id="ed-date" type="date" value={editForm.date} onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))} className={inputClass} data-testid="case-edit-date" />
                  </Field>
                  <Field label="Frist" htmlFor="ed-due">
                    <Input id="ed-due" type="date" value={editForm.dueDate} onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))} className={inputClass} data-testid="case-edit-due" />
                  </Field>
                </div>
                <Field label="Tildelt" htmlFor="ed-assigned">
                  <PersonSelect id="ed-assigned" value={editForm.assignedToId} onChange={(v) => setEditForm((f) => ({ ...f, assignedToId: v }))} refs={refs} refsState={refsState} emptyLabel="Ikke tildelt" testId="case-edit-assigned" />
                </Field>
                <Field label="Prosjekt" htmlFor="ed-project">
                  <RefSelect id="ed-project" value={editForm.projectId} onValueChange={(v) => setEditForm((f) => ({ ...f, projectId: v }))} allowEmpty emptyLabel="Ikke knyttet til prosjekt" list={refs?.projects} testId="case-edit-project" />
                </Field>
                <Field label={unit === 'km' ? 'Km-stand' : 'Timeteller'} htmlFor="ed-meter">
                  <Input id="ed-meter" type="number" inputMode="numeric" min={0} value={editForm.meterAt} onChange={(e) => setEditForm((f) => ({ ...f, meterAt: e.target.value }))} className={inputClass} data-testid="case-edit-meter" />
                </Field>
                <Field label="Årsak til endringen" htmlFor="ed-reason" hint="Valgfritt – vises i historikken">
                  <Input id="ed-reason" value={editForm.reason} maxLength={300} onChange={(e) => setEditForm((f) => ({ ...f, reason: e.target.value }))} className={inputClass} data-testid="case-edit-reason" />
                </Field>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setMode(null)} disabled={busy} data-testid="case-edit-cancel">Avbryt</Button>
                  <Button className={cn(primaryBtn, 'h-10 font-semibold')} onClick={submitEdit} disabled={busy} data-testid="case-edit-save">Lagre endringer</Button>
                </div>
              </div>
            </section>
          )}

          {!mode && (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3" data-testid="case-facts">
                <Fact label="Maskin" testId="case-fact-machine">
                  {ev.machine ? (
                    <Link to={`/maskiner/${ev.machine.machineId}`} className="font-medium text-[var(--hr-heading)] hover:underline" onClick={() => onOpenChange(false)} data-testid="case-machine-link">
                      {machineLabel(ev.machine)}
                    </Link>
                  ) : (
                    ev.machineId
                  )}
                </Fact>
                <Fact label="Tildelt" testId="case-fact-assigned">{ev.assignedTo?.name ?? <span className="text-[var(--hr-amber)]">Ikke tildelt</span>}</Fact>
                <Fact label="Frist" testId="case-fact-due">
                  <span className={cn(ev.overdue && 'font-semibold text-[var(--hr-amber)]')}>{ev.dueDate ? dateText(ev.dueDate) : '–'}</span>
                </Fact>
                <Fact label="Meldt av" testId="case-fact-reporter">{ev.reportedBy?.name ?? '–'}</Fact>
                <Fact label="Prosjekt" testId="case-fact-project">
                  {ev.project ? (
                    <Link to={`/prosjekter/${ev.project.id}`} className="hover:underline" onClick={() => onOpenChange(false)}>
                      {ev.project.name}
                    </Link>
                  ) : (
                    '–'
                  )}
                </Fact>
                <Fact label={unit === 'km' ? 'Km-stand' : 'Timeteller'} testId="case-fact-meter">{ev.meterAt !== null && ev.meterAt !== undefined ? `${fmtNumber(ev.meterAt, 0)} ${unit}` : '–'}</Fact>
              </dl>

              {ev.description && (
                <section>
                  <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted-2)]">Beskrivelse</h3>
                  <p className="mt-1 whitespace-pre-line text-[14px] leading-6 text-[var(--hr-text)]" data-testid="case-description">{ev.description}</p>
                </section>
              )}

              {ev.resolution && (
                <section className="rounded-[12px] border border-[rgba(6,118,71,0.25)] bg-[var(--hr-green-bg)] p-4" data-testid="case-resolution">
                  <h3 className="text-[14px] font-semibold text-[var(--hr-green)]">{ev.type === 'feilmelding' ? 'Løst' : 'Utført'} {dateText(ev.resolution.date)} · {ev.resolution.by?.name ?? 'ikke oppgitt'}</h3>
                  <p className="mt-1 text-[14px] leading-6 text-[var(--hr-text)]">{ev.resolution.work}</p>
                  {ev.resolution.meterAt !== null && ev.resolution.meterAt !== undefined && <p className="hr-tabular mt-1 text-[13px] text-[var(--hr-muted)]">Teller ved utført: {fmtNumber(ev.resolution.meterAt, 0)} {unit}</p>}
                </section>
              )}

              <section>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted-2)]">Vedlegg {ev.attachments?.length ? `(${ev.attachments.length})` : ''}</h3>
                  <input ref={fileRef} type="file" accept={ATTACHMENT_RULES.accept} multiple className="sr-only" onChange={(e) => addFiles(e.target.files)} data-testid="case-attachments-input" />
                  <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]" onClick={() => fileRef.current?.click()} disabled={busy || (ev.attachments?.length ?? 0) >= ATTACHMENT_RULES.maxPerEvent} data-testid="case-attachments-add">
                    <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Legg til
                  </Button>
                </div>
                {ev.attachments?.length ? (
                  <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="case-attachments">
                    {ev.attachments.map((att) => (
                      <AttachmentChip key={att.id} att={att} onDelete={removeAttachment} />
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-[13px] text-[var(--hr-muted)]">Ingen vedlegg. {ATTACHMENT_RULES.label}.</p>
                )}
              </section>

              <section data-testid="case-note-form">
                <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted-2)]">Nytt notat</h3>
                <div className="mt-2 space-y-2">
                  <Textarea value={noteForm.text} onChange={(e) => setNoteForm((f) => ({ ...f, text: e.target.value }))} rows={2} maxLength={2000} placeholder={noteForm.kind === 'arbeid' ? 'Hva ble gjort underveis?' : 'Observasjon, avtale eller status …'} aria-label="Notat" className={cn(textareaClass, 'min-h-[64px]')} data-testid="case-note-text" />
                  {errors.note && <p className="text-[13px] text-[var(--hr-red)]" role="alert">{errors.note}</p>}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="grid grid-cols-2 gap-1.5 sm:w-[220px]">
                      {[['notat', 'Notat'], ['arbeid', 'Utført arbeid']].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setNoteForm((f) => ({ ...f, kind: v }))} className={cn('h-9 rounded-[8px] border text-[13px] font-medium transition-colors duration-150', noteForm.kind === v ? 'border-[var(--hr-heading)] bg-[var(--hr-surface-2)] text-[var(--hr-heading)]' : 'border-[var(--hr-border)] bg-white text-[var(--hr-muted)] hover:bg-[var(--hr-surface-2)]')} aria-pressed={noteForm.kind === v} data-testid={`case-note-kind-${v}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <PersonSelect id="note-by" value={noteForm.byId} onChange={(v) => setNoteForm((f) => ({ ...f, byId: v }))} refs={refs} emptyLabel="Hvem?" testId="case-note-by" />
                    </div>
                    <Button variant="outline" className="h-10 rounded-[10px] bg-white sm:h-9" onClick={submitNote} disabled={busy} data-testid="case-note-save">
                      Legg til
                    </Button>
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted-2)]">Historikk</h3>
                  <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]" onClick={startEdit} disabled={busy} data-testid="case-edit">
                    <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Rediger sak
                  </Button>
                </div>
                <div className="mt-3">
                  <CaseTimeline ev={ev} />
                </div>
                <p className="mt-3 text-[12px] leading-5 text-[var(--hr-muted-2)]">Historikken er en ærlig eksempelmarkering («Kontor (eksempeldata)» og fiktive medarbeidere) – ikke en autentisert revisjonslogg.</p>
              </section>
            </>
          )}
        </div>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-[16px]" data-testid="case-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette saken?</AlertDialogTitle>
            <AlertDialogDescription>Saken, notatene og vedleggene fjernes fra eksempeldataene. Dette kan ikke angres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[10px]" data-testid="case-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="rounded-[10px] bg-[var(--hr-red)] text-white hover:bg-[#9a1d14]" onClick={remove} disabled={busy} data-testid="case-delete-confirm">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormSheet>
  );
}

/** Saksdetaljen følger URL-en (?sak=<id>), så rapporter og lister kan lenke rett til en sak. */
export function useCaseParam() {
  const [params, setParams] = useSearchParams();
  const caseId = params.get('sak');
  const openCase = useCallback(
    (id) => {
      const next = new URLSearchParams(params);
      next.set('sak', id);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const closeCase = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete('sak');
    setParams(next, { replace: true });
  }, [params, setParams]);
  return { caseId, openCase, closeCase };
}

export function CaseSheetHost({ onChanged, machine }) {
  const { caseId, closeCase } = useCaseParam();
  return <CaseSheet eventId={caseId} open={!!caseId} onOpenChange={(o) => !o && closeCase()} onChanged={onChanged} machine={machine} />;
}

/* ---------- Lister ---------- */

export const CASE_VIEWS = [
  { id: 'apne', label: 'Åpne', match: (e) => e.open },
  { id: 'feil', label: 'Feilmeldinger', match: (e) => e.type === 'feilmelding' },
  { id: 'service', label: 'Service og vedlikehold', match: (e) => e.type !== 'feilmelding' },
  { id: 'avsluttet', label: 'Avsluttede', match: (e) => !e.open },
  { id: 'alle', label: 'Alle', match: () => true },
];

export function CaseViewChips({ value, onChange, events, testId = 'case-view' }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Utvalg" data-testid={testId}>
      {CASE_VIEWS.map((v) => {
        const n = events.filter(v.match).length;
        const on = value === v.id;
        return (
          <button key={v.id} type="button" role="tab" aria-selected={on} onClick={() => onChange(v.id)} className={cn('inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', on ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid={`${testId}-${v.id}`}>
            {v.label}
            <span className={cn('hr-tabular text-[12px]', on ? 'text-white/80' : 'text-[var(--hr-muted-2)]')}>{n}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Saksliste: tabell på større skjermer, kort på mobil. Klikk åpner saksdetaljen. */
export function CaseList({ events, onOpen, showMachine = true, emptyText = 'Ingen saker.', testId = 'case-list', limit }) {
  const rows = limit ? events.slice(0, limit) : events;
  if (!events.length) return <p className="px-5 py-6 text-[14px] text-[var(--hr-muted)]" data-testid={`${testId}-empty`}>{emptyText}</p>;
  return (
    <>
      {/* Mobil: kort */}
      <ul className="divide-y divide-[var(--hr-hairline)] md:hidden" data-testid={`${testId}-cards`}>
        {rows.map((ev) => (
          <li key={ev.id}>
            <button type="button" onClick={() => onOpen(ev.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`${testId}-card-${ev.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <TypeMark type={ev.type} />
                  <span className="hr-tabular text-[13px] text-[var(--hr-muted)]" title={listDateTitle(ev)}>{dateText(listDate(ev))}</span>
                </div>
                <div className="mt-1 text-[15px] font-semibold leading-5 text-[var(--hr-heading)]">{ev.title}</div>
                {showMachine && ev.machine && <div className="text-[13px] text-[var(--hr-muted)]">{machineLabel(ev.machine)}</div>}
                <CaseBadges ev={ev} className="mt-1.5" />
                <div className="mt-1 text-[13px] text-[var(--hr-muted)]">
                  {ev.assignedTo ? `Tildelt ${ev.assignedTo.name}` : 'Ikke tildelt'}
                  {ev.dueDate && ev.open ? ` · frist ${dateText(ev.dueDate)}` : ''}
                </div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {/* Desktop: tabell. Sak-kolonnen er begrenset i bredde slik at tabellen får plass uten lokal sidescroll ved ~1157px. */}
      <div className="hidden overflow-x-auto md:block">
        <Table data-testid={`${testId}-table`}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[1%] whitespace-nowrap">Dato</TableHead>
              <TableHead>Sak</TableHead>
              {showMachine && <TableHead className="hidden whitespace-nowrap xl:table-cell">Maskin</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="hidden whitespace-nowrap lg:table-cell">Tildelt</TableHead>
              <TableHead className="hidden whitespace-nowrap xl:table-cell">Frist</TableHead>
              <TableHead className="w-[1%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((ev) => (
              <TableRow key={ev.id} className="cursor-pointer" onClick={() => onOpen(ev.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && onOpen(ev.id)} data-testid={`${testId}-row-${ev.id}`}>
                <TableCell className="hr-tabular whitespace-nowrap" title={listDateTitle(ev)}>{dateText(listDate(ev))}</TableCell>
                <TableCell>
                  <div className="max-w-[260px] lg:max-w-[300px] xl:max-w-[420px]">
                    <div className="flex min-w-0 items-center gap-2">
                      <TypeMark type={ev.type} />
                      <span className="min-w-0 truncate font-medium text-[var(--hr-heading)]">{ev.title}</span>
                    </div>
                    {showMachine && ev.machine && <div className="mt-0.5 truncate text-[13px] text-[var(--hr-muted)] xl:hidden">{machineLabel(ev.machine)}</div>}
                    {ev.description && <div className={cn('mt-0.5 line-clamp-1 text-[13px] text-[var(--hr-muted)]', showMachine && ev.machine && 'hidden xl:block')}>{ev.description}</div>}
                  </div>
                </TableCell>
                {showMachine && (
                  <TableCell className="hidden whitespace-nowrap xl:table-cell">
                    {ev.machine ? (
                      <Link to={`/maskiner/${ev.machine.machineId}`} onClick={(e) => e.stopPropagation()} className="font-medium text-[var(--hr-heading)] hover:underline">
                        {machineLabel(ev.machine)}
                      </Link>
                    ) : (
                      ev.machineId
                    )}
                  </TableCell>
                )}
                <TableCell><CaseBadges ev={ev} /></TableCell>
                <TableCell className="hidden whitespace-nowrap lg:table-cell">{ev.assignedTo?.name ?? <span className="text-[var(--hr-amber)]">Ikke tildelt</span>}</TableCell>
                <TableCell className={cn('hr-tabular hidden whitespace-nowrap xl:table-cell', ev.overdue && 'font-semibold text-[var(--hr-amber)]')}>{ev.open && ev.dueDate ? dateText(ev.dueDate) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
                <TableCell><ChevronRight className="h-4 w-4 text-[var(--hr-muted-2)]" aria-hidden="true" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {limit && events.length > limit && <p className="px-5 py-2 text-[13px] text-[var(--hr-muted)]">Viser {limit} av {events.length}.</p>}
    </>
  );
}

/* ---------- «Hva trenger oppfølging?» ---------- */

const TONE_TEXT = { red: 'text-[var(--hr-red)]', amber: 'text-[var(--hr-amber)]', neutral: 'text-[var(--hr-muted)]', navy: 'text-[var(--hr-navy)]' };

/**
 * Maskiner som trenger oppfølging, med årsak og den viktigste åpne saken. Sortert etter alvor:
 * kan ikke brukes → avventer frigivelse → forfalt frist/service → service snart/ukjent → åpne saker.
 */
export function AttentionList({ machines, events, onOpenCase, limit, compact = false, testId = 'attention' }) {
  const items = useMemo(() => {
    const rank = (m) => (m.operational?.status === 'stopp' ? 0 : m.operational?.status === 'avklaring' ? 1 : m.cases?.overdue || m.service?.status === 'forfalt' ? 2 : m.service?.status === 'snart' || m.service?.status === 'ukjent' ? 3 : 4);
    return (machines ?? [])
      .filter((m) => m.needsFollowUp)
      .map((m) => {
        const mine = (events ?? []).filter((e) => e.machineId === m.machineId && e.open);
        mine.sort((a, b) => (b.blocksUse ? 1 : 0) - (a.blocksUse ? 1 : 0) || (b.overdue ? 1 : 0) - (a.overdue ? 1 : 0) || (a.type === 'feilmelding' ? -1 : 1));
        const awaiting = (events ?? []).find((e) => e.machineId === m.machineId && e.awaitingClearance);
        // Driftsstatus vises som egen pille – ikke gjenta den i årsakslisten.
        return { m, reasons: followUpReasons(m).filter((r) => r.id !== 'stopp' && r.id !== 'avklaring'), top: awaiting ?? mine[0] ?? null, more: Math.max(0, mine.length - 1) };
      })
      .sort((a, b) => rank(a.m) - rank(b.m) || a.m.internalNo.localeCompare(b.m.internalNo));
  }, [machines, events]);

  if (!items.length) {
    return (
      <div className="flex items-center gap-2 px-5 py-5 text-[14px] text-[var(--hr-muted)]" data-testid={`${testId}-empty`}>
        <CheckCircle2 className="h-4 w-4 text-[var(--hr-green)]" aria-hidden="true" /> Ingen maskiner trenger oppfølging akkurat nå.
      </div>
    );
  }
  const rows = limit ? items.slice(0, limit) : items;
  return (
    <ul className="divide-y divide-[var(--hr-hairline)]" data-testid={`${testId}-list`}>
      {rows.map(({ m, reasons, top, more }) => (
        <li key={m.machineId} className={cn('flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-4 sm:px-5', compact && 'py-2.5')} data-testid={`${testId}-item-${m.machineId}`}>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link to={`/maskiner/${m.machineId}`} className="flex items-center gap-2 font-semibold text-[var(--hr-heading)] hover:underline" data-testid={`${testId}-machine-${m.machineId}`}>
                <span className="hr-num rounded-[6px] border border-[var(--hr-hairline)] bg-white px-1.5 py-0.5 text-[13px]">{m.internalNo}</span>
                <span className="text-[14px]">{m.name}</span>
              </Link>
              <OperationalPill status={m.operational.status} label={m.operational.label} data-testid={`${testId}-operational-${m.machineId}`} />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[13px]">
              {reasons.map((r) => (
                <span key={r.id} className={cn('font-medium', TONE_TEXT[r.tone])}>{r.label}</span>
              ))}
              {m.service?.status !== 'ok' && m.service && <span className="text-[var(--hr-muted-2)]">· {serviceLine(m.service, { short: true })}</span>}
            </div>
            {top && !compact && (
              <button type="button" onClick={() => onOpenCase?.(top.id)} className="mt-1.5 flex w-full max-w-full flex-col items-start gap-y-0.5 rounded-[8px] text-left text-[14px] text-[var(--hr-text)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] sm:flex-row sm:items-center sm:gap-x-1.5" data-testid={`${testId}-case-${m.machineId}`}>
                <span className="flex min-w-0 max-w-full items-center gap-1.5">
                  <TypeMark type={top.type} />
                  <span className="min-w-0 truncate">{top.title}</span>
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)] sm:hidden" aria-hidden="true" />
                </span>
                <span className="shrink-0 text-[13px] text-[var(--hr-muted)]">
                  <span className="hidden sm:inline">· </span>
                  {EVENT_STATUS_LABELS[top.status]}{top.assignedTo ? ` · ${top.assignedTo.name}` : ''}{more ? ` · +${more}` : ''}
                </span>
                <ArrowUpRight className="hidden h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)] sm:block" aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {top && compact && (
              <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]" onClick={() => onOpenCase?.(top.id)} data-testid={`${testId}-open-case-${m.machineId}`}>
                Sak <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
            <Button asChild variant="outline" size="sm" className="h-8 rounded-[8px] bg-white px-2.5 text-[13px]">
              <Link to={`/maskiner/${m.machineId}`} data-testid={`${testId}-open-machine-${m.machineId}`}>Maskinkort</Link>
            </Button>
          </div>
        </li>
      ))}
      {limit && items.length > limit && (
        <li className="px-5 py-2.5 text-[13px] text-[var(--hr-muted)]">
          +{items.length - limit} til.{' '}
          <Link to="/maskiner?fane=maskinpark" className="font-medium text-[var(--hr-accent-text)] hover:underline" data-testid={`${testId}-all-link`}>
            Se alle i Maskiner
          </Link>
        </li>
      )}
    </ul>
  );
}

/* ---------- Maskinkort: rolig statuskort ---------- */

/**
 * Statuskort for én maskin. Skiller tydelig registerets teller (livstid) fra registrert maskintid i valgt periode,
 * og viser serviceplanen ærlig («Ukjent» når grunnlag mangler).
 */
export function MachineStatusCard({ m, periodLabel, actions, className }) {
  const unit = meterUnit(m);
  const plan = m.servicePlan ?? {};
  const s = m.service ?? {};
  const kindLabel = plan.kind === 'dato' ? 'dato' : plan.kind === 'km' ? 'km' : 'timer';
  const intervalText = plan.intervalValue ? `hver ${fmtNumber(plan.intervalValue, 0)} ${plan.kind === 'dato' ? 'dager' : plan.kind === 'km' ? 'km' : 't'}` : 'intervall ikke satt';
  const lastText =
    plan.kind === 'dato'
      ? plan.lastDate
        ? `sist ${dateText(plan.lastDate)}`
        : 'siste service ukjent'
      : plan.lastAt !== null && plan.lastAt !== undefined
        ? `sist ved ${fmtNumber(plan.lastAt, 0)} ${unit}${plan.lastDate ? ` (${dateText(plan.lastDate)})` : ''}`
        : 'siste service ukjent';
  const cases = m.cases ?? {};
  return (
    <section className={cn('rounded-[12px] border border-[var(--hr-hairline)] bg-white shadow-[var(--hr-shadow-xs)]', className)} data-testid="machine-status-card">
      <div className="flex flex-col gap-3 border-b border-[var(--hr-hairline)] px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--hr-muted-2)]">Maskinkort · eksempeldata</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <OperationalPill status={m.operational?.status} label={m.operational?.label} className="h-7 px-2.5 text-[13px]" data-testid="machine-operational-status" />
            <span className="text-[14px] text-[var(--hr-muted)]" data-testid="machine-operational-reason">{m.operational?.reason}</span>
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <dl className="grid grid-cols-2 gap-px bg-[var(--hr-hairline)] lg:grid-cols-4">
        <div className="bg-white px-5 py-3.5" data-testid="machine-card-meter">
          <dt className="text-[13px] text-[var(--hr-muted)]">{unit === 'km' ? 'Km-stand' : 'Timeteller'} <span className="text-[var(--hr-muted-2)]">(register, livstid)</span></dt>
          <dd className="hr-num mt-0.5 text-[20px] font-semibold leading-7 tracking-tight text-[var(--hr-heading)]">{m.meter === null || m.meter === undefined ? <span className="text-[16px] text-[var(--hr-amber)]">mangler</span> : `${fmtNumber(m.meter, 0)} ${unit}`}</dd>
          <dd className="text-[13px] text-[var(--hr-muted-2)]">{m.meterUpdatedAt ? `avlest ${dateText(m.meterUpdatedAt)}` : 'ikke datert'}</dd>
        </div>
        <div className="bg-white px-5 py-3.5" data-testid="machine-card-hours">
          <dt className="text-[13px] text-[var(--hr-muted)]">Registrert maskintid <span className="text-[var(--hr-muted-2)]">(timeføring)</span></dt>
          <dd className="hr-num mt-0.5 text-[20px] font-semibold leading-7 tracking-tight text-[var(--hr-heading)]">{fmtNumber(m.timeSums?.machineHours ?? 0, 1)} t</dd>
          <dd className="truncate text-[13px] text-[var(--hr-muted-2)]">{periodLabel ?? 'valgt periode'} · {m.timeSums?.count ?? 0} føringer{m.timeSumsTotal ? ` · totalt ${fmtNumber(m.timeSumsTotal.machineHours, 0)} t` : ''}</dd>
        </div>
        <div className="bg-white px-5 py-3.5" data-testid="machine-card-service">
          <dt className="text-[13px] text-[var(--hr-muted)]">Service <span className="text-[var(--hr-muted-2)]">({plan.exampleLabel ?? 'Eksempelplan'})</span></dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2">
            <ServiceStatusPill status={s.status} data-testid="machine-service-status" />
          </dd>
          <dd className={cn('mt-0.5 text-[13px]', s.status === 'ukjent' ? 'text-[var(--hr-amber)]' : s.status === 'forfalt' ? 'text-[var(--hr-red)]' : 'text-[var(--hr-muted-2)]')} data-testid="machine-service-line">{serviceLine(s)}</dd>
        </div>
        <div className="bg-white px-5 py-3.5" data-testid="machine-card-cases">
          <dt className="text-[13px] text-[var(--hr-muted)]">Åpne saker</dt>
          <dd className="hr-num mt-0.5 text-[20px] font-semibold leading-7 tracking-tight text-[var(--hr-heading)]">{fmtNumber(cases.open ?? 0, 0)}</dd>
          <dd className={cn('text-[13px]', cases.overdue ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>
            {cases.openFaults ?? 0} feil · {cases.openService ?? 0} service{cases.overdue ? ` · ${cases.overdue} forfalt frist` : ''}
          </dd>
        </div>
      </dl>
      <div className="grid gap-x-6 gap-y-2 border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] text-[var(--hr-muted)] sm:grid-cols-2 lg:grid-cols-4">
        <div><span className="text-[var(--hr-muted-2)]">Type</span> · {m.typeLabel ?? m.type}{m.modelYear ? ` ${m.modelYear}` : ''}</div>
        <div><span className="text-[var(--hr-muted-2)]">Plassering</span> · {m.location ?? '–'}</div>
        <div><span className="text-[var(--hr-muted-2)]">Ansvarlig</span> · {m.responsible ? m.responsible.name : '–'}</div>
        <div data-testid="machine-service-plan"><span className="text-[var(--hr-muted-2)]">Serviceplan</span> · etter {kindLabel}, {intervalText}, {lastText}</div>
      </div>
    </section>
  );
}
